import "flatpickr/dist/flatpickr.min.css";

import React, { useCallback, useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import cup_icon from "../../../assets/cup_icon.svg";
import empty_page from "../../../assets/selection_placeholder.svg";
import two_stars_icon from "../../../assets/two_stars_icon.svg";
import { USER_TYPE } from "../../../constants/common";
import { useTheme } from "../../../context/ThemeContext";
import useAuth from "../../../hooks/useAuth";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import teacherServices from "../../../services/teacherServices";
import { selectStylesV2 } from "../../../styles/selectStyles";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const TopWeeklyStudents = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithLang();
  const { theme } = useTheme();
  const lang = getLanguage();
  const user = useAuth();

  const [loadingTopStudents, setLoadingTopStudents] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [teachers, setTeachers] = useState([]);

  const handleSelectClassroom = (option) => {
    setSelectedClassroom(option);
  };

  const handleSelectTeacher = (option) => {
    setSelectedTeacher(option);
    if (option) {
      setSelectedClassroom(null);
      fetchClassrooms(option.value);
    } else {
      setClassrooms([]);
      setSelectedClassroom(null);
    }
  };

  const fetchClassrooms = useCallback(async (teacherID) => {
    setLoadingClassrooms(true);
    try {
      const res = await teacherServices.getClassroomsByTeacher(teacherID);
      const formattedClassrooms = res.data.map((classroom) => ({
        ...classroom,
        label: classroom.name,
        value: classroom.id,
      }));
      setClassrooms(formattedClassrooms);
    } catch (error) {
      console.error("Error fetching classrooms:", error);
    } finally {
      setLoadingClassrooms(false);
    }
  }, []);

  const getTeachers = async () => {
    setLoadingTeachers(true);
    try {
      const response = await schoolServices.getTeachersBySchool(user?.school?.id);
      const formattedTeachers = response.data
        .filter((item) => !item.is_disabled)
        .map((teacher) => ({
          label: teacher.name,
          value: teacher.id,
        }));

      setTeachers(formattedTeachers);
    } catch (error) {
      console.error("Error fetching teachers data:", error);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const fetchTopWeeklyStudents = useCallback(async () => {
    if (!selectedClassroom || !selectedDate) return;

    setLoadingTopStudents(true);
    try {
      const startDate = selectedDate[0];
      const endDate = selectedDate[1] || selectedDate[0];

      const formatDate = (date) => date.toLocaleDateString("en-CA"); // outputs YYYY-MM-DD in local timezone

      const payload = {
        params: {
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
          limit: 5,
        },
      };

      const response = await classroomServices.getClassroomTopStudents(selectedClassroom.value, payload);
      setStudents(response);
    } catch (error) {
      console.error("Error fetching top weekly students:", error);
    } finally {
      setLoadingTopStudents(false);
    }
  }, [selectedClassroom, selectedDate]);

  useEffect(() => {
    getTeachers();
  }, [lang]);

  useEffect(() => {
    fetchTopWeeklyStudents();
  }, [selectedClassroom, selectedDate, fetchTopWeeklyStudents]);

  const navigateStudentDetails = (id) => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/students/${id}`);
  };

  const navigateTeacherDetails = (id) => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/teachers/${id}`);
  };

  const navigateClassroomDetails = (id) => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/classrooms/${id}`);
  };

  return (
    <div className="relative">
      <div className="mb-6 flex flex-col justify-between space-y-4 lg:flex-row lg:space-y-0">
        <h1 className="flex items-center space-x-6 text-xl font-semibold text-[#FFBF0F] xl:text-3xl">
          <img src={cup_icon} alt="cup" />
          <span>{t("top_five_students")}</span>
        </h1>
      </div>

      <div className="mb-6 flex items-center justify-between space-x-2">
        <Select
          className="w-1/3"
          isClearable
          isLoading={loadingTeachers}
          options={teachers}
          value={selectedTeacher}
          onChange={handleSelectTeacher}
          placeholder={t("select_teacher")}
          styles={selectStylesV2(theme)}
          noOptionsMessage={() => <p>{t("no_options")}</p>}
          loadingMessage={() => <p>{t("loading")}</p>}
        />

        <Select
          className="w-1/3"
          isClearable
          isLoading={loadingClassrooms}
          options={classrooms}
          value={selectedClassroom}
          onChange={handleSelectClassroom}
          placeholder={t("select_classroom")}
          styles={selectStylesV2(theme)}
          noOptionsMessage={() => <p>{t("no_options")}</p>}
          loadingMessage={() => <p>{t("loading")}</p>}
        />

        <div className="flex items-center space-x-2 xl:space-x-4">
          <label className="flex items-center text-sm xl:text-lg">
            <span className="mx-3 text-sm text-blue-500">{t("date")}: </span>
            <Flatpickr
              placeholder={t("select_date")}
              className="block w-full rounded-md border bg-[#F9FAFB] px-3 py-2 text-sm placeholder:text-sm placeholder:text-mutedText focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-10 disabled:text-gray-200 dark:border-dark-background dark:bg-dark-background dark:text-dark-mutedText"
              options={{ dateFormat: "d/m/Y", mode: "single" }} // change mode to single
              value={selectedDate}
              onChange={(date) => {
                if (date.length) {
                  const startDate = date[0];
                  const endDate = new Date(startDate);
                  endDate.setDate(startDate.getDate() + 6);
                  setSelectedDate([startDate, endDate]); // keep range logic internally
                }
              }}
            />
          </label>
          <div className="ml-4 text-xs italic text-gray-500 dark:text-gray-100">
            <span>{t("updated_every_sunday")}</span>
          </div>
        </div>
      </div>

      {(!selectedTeacher || !selectedClassroom) && (
        <div className="flex h-96 flex-col items-center justify-center space-y-9">
          <img height={"22%"} width={"22%"} className="opacity-75" src={empty_page} alt="" />
          <p className="text-lg font-medium text-gray-200">
            {t("select_teacher_and_classroom_to_view_students")}
          </p>
        </div>
      )}

      {selectedClassroom && students?.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl shadow-md">
            <table className="w-full min-w-[400px] table-auto">
              <thead>
                <tr className="bg-blue-500 text-left text-white">
                  <th className="w-12 p-3 text-center font-light">#</th>
                  <th className="p-3 text-justify text-lg font-light">{t("student")}</th>
                  <th className="p-3 text-justify text-lg font-light">{t("teacher")}</th>
                  <th className="p-3 text-justify text-lg font-light">{t("classroom")}</th>
                  <th className="p-3 text-justify text-lg font-light">{t("points")}</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, idx) => (
                  <tr key={student.id} className="bg-blue-500 text-white">
                    <td className="w-12 p-3 text-center text-sm xl:text-xl">{idx + 1}.</td>
                    <td className="p-3 text-sm xl:text-xl">
                      <span
                        onClick={() => navigateStudentDetails(student?.student_id)}
                        className="cursor-pointer hover:underline"
                      >
                        {student?.student_name}
                      </span>
                    </td>
                    <td className="p-3 text-sm xl:text-xl">
                      <span
                        onClick={() => navigateTeacherDetails(selectedTeacher?.value)}
                        className="cursor-pointer hover:underline"
                      >
                        {selectedTeacher?.label}
                      </span>
                    </td>
                    <td className="p-3 text-sm xl:text-xl">
                      <span
                        onClick={() => navigateClassroomDetails(selectedClassroom?.value)}
                        className="cursor-pointer hover:underline"
                      >
                        {selectedClassroom?.label}
                      </span>
                    </td>
                    <td className="p-3 text-sm font-semibold xl:text-xl">{student?.total_points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <img
            className={`absolute top-32 hidden xl:block ltr:right-[-27px] rtl:left-[-27px]`}
            src={two_stars_icon}
            alt="star"
          />
        </>
      )}

      {selectedClassroom && !loadingTopStudents && students?.length === 0 && (
        <p className="mt-6 text-center text-gray-400">{t("no_data_available")}</p>
      )}
    </div>
  );
};

export default TopWeeklyStudents;
